// @i18n-ignore-file
import { useStatsigClient } from '@statsig/react-bindings'

import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'

import { DEFAULT_STATSIG_ECOMM_SSR_DYNAMIC_CONFIG } from './statsig-dynamic-config.constants'

// Lives in the shared `ecomm-ssr` dynamic config (not `pl_widget_ids`, which is strictly the
// slot→widget-id map). Console: dynamic_configs/ecomm-ssr → plEagerLayoutPaginationMaxPages
const ECOMM_SSR_CONFIG_NAME = 'ecomm-ssr'
const MAX_PAGES_CONFIG_KEY = 'plEagerLayoutPaginationMaxPages'
const DEFAULT_MAX_PAGES = DEFAULT_STATSIG_ECOMM_SSR_DYNAMIC_CONFIG.plEagerLayoutPaginationMaxPages

/**
 * Safety cap for PL eager layout pagination: how many pages the eager loop (home) may drain from
 * mount before standing down to normal scroll-triggered pagination. Statsig-tunable so the cap can
 * move without an SSR deploy; `0` turns eager draining off entirely (kill switch).
 *
 * Error-safe like `useStatsigPlWidgetId`: an unavailable client or a malformed value (negative,
 * fractional, non-numeric) falls back to the code default — a config problem must never break the
 * page or un-cap the drain.
 *
 * `getDynamicConfig` logs a client-side exposure, so call this only on surfaces that actually
 * eager-drain (today: the home PL renderer, which passes it into LayoutPaginationWrapper) — not in
 * shared components that CLP/PDP/search also render.
 */
export const useStatsigPlEagerLayoutMaxPages = (): number => {
  const { getDynamicConfig } = useStatsigClient()
  try {
    const value = getDynamicConfig(ECOMM_SSR_CONFIG_NAME).get(MAX_PAGES_CONFIG_KEY, DEFAULT_MAX_PAGES)
    return Number.isInteger(value) && value >= 0 ? value : DEFAULT_MAX_PAGES
  } catch (error) {
    captureException(error as Error, {
      module: MODULES.PL,
      feature: FEATURES.LAYOUT_PAGINATION,
      additionalTags: { source: 'useStatsigPlEagerLayoutMaxPages' },
      extraInfo: { configName: ECOMM_SSR_CONFIG_NAME, configKey: MAX_PAGES_CONFIG_KEY },
    })
    return DEFAULT_MAX_PAGES
  }
}
